// apps/client/src/pages/admin/dashboard/client/page.tsx

import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  CheckCircleIcon,
  DotsThreeIcon,
  ExportIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  UserCheckIcon,
  UserMinusIcon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
  PencilIcon,
} from "@phosphor-icons/react";
import { usefilterUser } from "@/client/services/user/filter-user";
import { userFilterUserStore } from "@/client/stores/filter-user";
import { useDialog } from "@/client/stores/dialog";
import { useExportUser } from "@/client/services/user/export-xlsx";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";
import { FilterUserType } from "@reactive-resume/dto";
import { SelectRangeDate } from "../_components/date-range-overview";
import { subDays } from "date-fns";
import { useUserStats } from "@/client/services/user/user-stats";
import { cn } from "@reactive-resume/utils";
import { useSearchParams } from "react-router";
import { useChangeActivation } from "@/client/services/user/activate-user";
import { queryClient } from "@/client/libs/query-client";
// import { ManualCreateUserDialog } from "./components/ManualCreateUserDialog";
// import { FilterClientDialog } from "./components/FilterClientDialog";
// import { GetUserDialog } from "@/pages/admin/dashboard/admin/components/GetUserDialog";

export const UserAdminPage = () => {
  const { filterUser, replaceFilterUser } = userFilterUserStore();
  const [page, setPage] = useState(1);
  // Lien profond (ex. « Fiche client » depuis le centre d'aide) : ?search=…
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");
  const [verified, setVerified] = useState<"all" | "yes" | "no">("all");

  const { filterUsers } = usefilterUser(false, page, filterUser);
  const { refetch } = useExportUser();
  const { ChStatus } = useChangeActivation();

  // Dates pour les statistiques
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const { data: stats, isLoading: statsLoading } = useUserStats(dateRange.from, dateRange.to);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["userStats"] });
  }, [filterUsers]);

  const dialogCreate = useDialog("create-user");
  const advancedFilters = useDialog("filter-client");
  const profileDialog = useDialog<{ id: string }>("get-user");

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecificpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter: FilterUserType = { ...filterUser };
    delete nextFilter.name;
    delete nextFilter.email;
    delete nextFilter.isActivated;
    delete nextFilter.emailVerified;

    if (search.trim()) {
      const searchField = search.includes("@") ? "email" : "name";
      nextFilter[searchField] = { contains: search.trim() };
    }
    if (status !== "all") nextFilter.isActivated = { equals: status === "active" };
    if (verified !== "all") nextFilter.emailVerified = { equals: verified === "yes" };

    setPage(1);
    replaceFilterUser(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setVerified("all");
    setPage(1);
    replaceFilterUser(null);
  };

  // Ouvrir le profil
  const openProfile = (userId: string) => {
    profileDialog.open("read", { id: "get-user", item: { id: userId } });
  };

  // Ouvrir la modification
  const openEdit = (item: any) => {
    dialogCreate.open("update", {
      id: "create-user",
      item: {
        userId: item.id,
        isInternal: item.isInternal,
        user: {
          name: item.name,
          email: item.email,
          isInternal: item.isInternal,
        },
        currentRoles: item.roles || [],
      },
    });
  };

  // Colonnes du tableau
  const columns: Column<any>[] = [
    {
      key: "name",
      header: "Utilisateur",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-admin-surface-elevated">
            <img
              src={item.picture || "/default-avatar.png"}
              alt={item.name}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-admin-text-primary">{item.name || "—"}</span>
            <span className="text-xs text-admin-text-muted">{item.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: "createdAt",
      header: "Inscription",
      render: (item) =>
        new Date(item.createdAt).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      key: "updatedAt",
      header: "Dernière activité",
      render: (item) =>
        new Date(item.updatedAt).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      key: "emailVerified",
      header: "Vérifié",
      align: "center",
      render: (item) =>
        item.emailVerified ? (
          <CheckCircleIcon size={20} weight="fill" className="text-admin-success" />
        ) : (
          <XCircleIcon size={20} weight="fill" className="text-admin-text-disabled" />
        ),
    },
    {
      key: "isActivated",
      header: "Statut",
      render: (item) => (
        <Badge variant={item.isActivated ? "success" : "error"}>
          {item.isActivated ? "Actif" : "Banni"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <div className="flex justify-end gap-1 text-admin-text-secondary">
          {/* Toggle activation */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Activer/Désactiver"
            onClick={() => ChStatus(item.id)}
            className={cn(
              "hover:text-admin-text-primary",
              item.isActivated ? "text-admin-success" : "text-admin-danger",
            )}
          >
            {item.isActivated ? <UserCheckIcon size={18} /> : <UserMinusIcon size={18} />}
          </Button>

          {/* Dropdown menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Plus d'actions"
                className="hover:text-admin-text-primary"
              >
                <DotsThreeIcon size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => openProfile(item.id)} className="cursor-pointer">
                <EyeIcon size={16} className="mr-2" /> Voir le profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openEdit(item)} className="cursor-pointer">
                <PencilIcon size={16} className="mr-2" /> Modifier
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Utilisateurs</h1>
          <p className="mt-1 text-sm text-admin-text-muted">Tableau de bord / Utilisateurs</p>
        </div>
        {/* <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder="Rechercher..."
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full bg-admin-surface text-admin-text-secondary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="w-8 h-8 rounded-full bg-admin-primary overflow-hidden">
            <img src="/avatar.jpg" alt="User" className="w-full h-full object-cover" />
          </div>
        </div> */}
      </div>

      {/* Sélecteur de dates pour les statistiques */}
      <div className="mb-6 flex justify-end">
        <SelectRangeDate
          value={dateRange}
          onChange={(value) => {
            if (value?.from) setDateRange({ from: value.from, to: value.to ?? value.from });
          }}
        />
      </div>

      {/* Cartes de métriques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Total Utilisateurs"
              number={statsLoading ? "..." : (stats?.total.toString() ?? "0")}
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5 text-xs text-admin-text-muted">Sur la période sélectionnée</div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Nouveaux (7 jours)"
              number={statsLoading ? "..." : (stats?.newUsers.toString() ?? "0")}
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <UserPlusIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5 text-xs text-admin-text-muted">
            Inscrits les 7 derniers jours
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Utilisateurs "
              number={statsLoading ? "..." : (stats?.active.toString() ?? "0")}
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <UserCheckIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5 text-xs text-admin-text-muted">
            Avec une souscription active
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Utilisateurs bannis"
              number={statsLoading ? "..." : (stats?.banned.toString() ?? "0")}
            />
            <div className="rounded-full bg-admin-danger/20 p-3 text-admin-danger">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5 text-xs text-admin-text-muted">Comptes désactivés</div>
        </CardRoot>
      </div>

      {/* Filtres et actions */}
      <div className="mb-6 rounded-xl-admin border border-admin-border bg-admin-surface p-4 sm:mb-8 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:gap-5">
          {/* Recherche */}
          <div className="relative w-full">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder="Rechercher par nom ou email..."
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres */}
          <div className="grid grid-cols-1 flex-row flex-wrap gap-3 sm:grid-cols-2 sm:gap-4 lg:flex">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Statut</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 w-full bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Banni</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Vérifié</span>
              <Select
                value={verified}
                onValueChange={(value) => setVerified(value as typeof verified)}
              >
                <SelectTrigger className="h-10 w-full bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="yes">Oui</SelectItem>
                  <SelectItem value="no">Non</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={applyQuickFilters}
              className="h-10 w-full border-admin-border bg-admin-background text-admin-text-secondary hover:text-admin-text-primary lg:mt-5 lg:w-auto"
            >
              <FunnelIcon size={16} className="mr-2" /> Filtrer
            </Button>
            <Button
              variant="outline"
              onClick={() => advancedFilters.open("create", { id: "filter-client" })}
              className="h-10 w-full border-admin-border bg-admin-background text-admin-text-secondary hover:text-admin-text-primary lg:mt-5 lg:w-auto"
            >
              <FunnelIcon size={16} className="mr-2" /> Filtres avancés
            </Button>
            {/* </div>

          Boutons d'action 
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 sm:gap-3">
             */}

            <Button
              variant="ghost"
              onClick={resetFilters}
              className="h-10 w-full text-admin-text-muted hover:text-admin-text-primary lg:mt-5 lg:w-auto"
            >
              Réinitialiser
            </Button>
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="h-10 w-full bg-admin-background text-admin-text-secondary hover:text-admin-text-primary lg:mt-5 lg:w-auto"
            >
              <ExportIcon size={16} className="mr-2" /> Exporter
            </Button>
            <Button
              onClick={() =>
                dialogCreate.open("create", {
                  id: "create-user",
                  item: { isInternal: false },
                })
              }
              className="col-span-2 h-10 w-full bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90 sm:col-span-1 lg:mt-5 lg:w-auto"
            >
              <UserPlusIcon size={16} className="mr-2" /> Nouvel utilisateur
            </Button>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <DataTable
        title=""
        data={filterUsers?.data ?? []}
        columns={columns}
        pageSize={filterUsers?.data?.length || 10}
        className="shadow-sm-admin"
      />

      {/* Pagination manuelle */}
      <div className="my-3 flex flex-wrap items-center justify-end gap-2 sm:gap-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={28} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}
        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {filterUsers && page + 1 <= filterUsers.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {filterUsers && page + 2 <= filterUsers.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecificpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {filterUsers && page < filterUsers.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={28} weight="regular" />
          </Button>
        )}
      </div>

      {/* Dialogues
      <GetUserDialog />
      <ManualCreateUserDialog />
      <FilterClientDialog /> */}
    </>
  );
};
